import React, { useEffect, useState } from 'react';
import {
  Grid,
  MenuItem,
  Chip,
  Box,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Controls } from '../components/controls/Controls';
import SettingsInputSwitcher from '../components/Settings/SettingsController/SettingsInputSwitcher';
import { useApiClient } from '../context/ApiClientContext';
import AppointmentTypeSettingButton from './integrations/AppointmentTypeSetting/AppointmentTypeSettingButton';
import generateIntakePlanLabel from '../utils/generateIntakePlanLabel';

export default function EditSettingsOptions(props) {
  const {
    setValues,
    handleSaveClick,
    values,
    newSetting,
    refreshParent,
    setErrorMessage,
    setSuccessMessage,
    isCreateMode,
    contextType,
  } = props;

  const [intakePlans, setIntakePlans] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [qrCodeIntakePlan, setQrCodeIntakePlan] = useState({});
  const apiClient = useApiClient();
  const { siteId, customerId } = useParams();

  const booleanSettingList = [
    'accuroEnabled',
    'accuroFilterByProviderIds',
    'accuroPreBookedAutomationEnabled',
    'accuroSameDayAutomationEnabled',
    'athenaEnabled',
    'canEditReport',
    'consentPageLogoOn',
    'disagreeButtonDisabled',
    'enableFeedbackText',
    'preventEmptyFreetextResponses',
    'patientCanDownloadReportOnFeedbackQuestion',
    'generateDDx',
    'generateMedsCheckPmrPdf',
    'notificationEnabled',
    'pccEnabled',
    'privacyPolicyEnabled',
    'pushToEndpointEnabled',
    'qrUseAgeInsteadOfDob',
    'requireUniqueId',
    'excludeChiefComplaintInHistory',
    'sendsTestDrives',
    'sftpAutomationEnabled',
    'skipCaptcha',
    'useImperialUnitsWhenReportingHeightAndWeight',
    'useMedicalNameWhenReportingSymptoms',
    'keepPiiAfterEmrPush',
    'telusEnabled',
    'llmSummaryEnabled',
    'enableLongitudinalHistory',
    'includeIcdCode',
    'includeMinPractitionerScope',
    'translatePatientFreeTextToEnglish',
    'scribeEnabled',
  ];

  const logoSettingList = [
    'consentPageLogo',
    'thankYouPageLogo',
    'disagreePageLogo',
    'qrCodePageLogo',
  ];

  const appointmentTypeSettingList = [
    'accuroAppointmentType',
    'advancedMDAppointmentTypes',
    'ecwAppointmentTypes',
  ];

  const dropdownSettingList = [
    'languageOptions',
    'localization',
    'smsProvider',
    'customTheme',
    'medicationFormType',
    'smsFromPhoneNumber',
    'llmOutputLocation',
    'scribeNoteVerbosity',
  ];

  const multilineSettingList = [
    'scribeNoteTemplate',
    'llmSummaryPrompt',
    'DDxPrompt',
  ];

  const optionsMapping = {
    languageOptions: {
      options: [
        { label: 'english', value: 'english' },
        { label: 'arabic', value: 'arabic' },
        { label: 'french-canadian', value: 'french-canadian' },
        { label: 'spanish-us', value: 'spanish-us' },
        { label: 'chinese-simplified', value: 'chinese-simplified' },
        { label: 'chinese-traditional', value: 'chinese-traditional' },
      ],
      isMultiple: true, // Multiple selection
    },
    localization: {
      options: [
        { label: 'en', value: 'en' },
        { label: 'ar', value: 'ar' },
        { label: 'fr_CA', value: 'fr_CA' },
        { label: 'es_US', value: 'es_US' },
        { label: 'zh_CN', value: 'zh_CN' },
        { label: 'zh_TW', value: 'zh_TW' },
      ],
      isMultiple: false, // Single selection
    },
    llmOutputLocation: {
      options: [
        { label: 'above report', value: 'above report' },
        { label: 'below report', value: 'below report' },
        { label: 'replace report', value: 'replace report' },
      ],
      isMultiple: false,
    },
    smsProvider: {
      options: [
        { label: 'twilio', value: 'twilio' },
        { label: 'kaleyra', value: 'kaleyra' },
      ],
      isMultiple: false,
    },
    customTheme: {
      options: [
        { label: 'none', value: 'none' },
        { label: 'maple', value: 'maple' },
      ],
      isMultiple: false,
    },
    medicationFormType: {
      options: [
        { label: 'simple', value: 'simple' },
        { label: 'v2', value: 'v2' },
      ],
      isMultiple: false,
    },
    smsFromPhoneNumber: {
      options: [
        {
          label: 'Toll-free - +18339473212 (Default)',
          value: '+18339473212',
        },
        {
          label: 'Toronto, ON, Canada - +16476975297',
          value: '+16476975297',
        },
        {
          label: 'San Francisco, CA, US - +14154494008 (Rocket Doctor US)',
          value: '+14154494008',
        },
        {
          label: 'Toronto, ON, Canada - +16474902708',
          value: '+16474902708',
        },
        {
          label: 'Bathurst, NB, Canada - +15065000765',
          value: '+15065000765',
        },
        {
          label: 'Montreal, QC, Canada - +14388017486',
          value: '+14388017486',
        },
        {
          label: 'North Van, BC, Canada - +16042105688',
          value: '+16042105688',
        },
        {
          label: 'UK - +447447191290',
          value: '+447447191290',
        },
      ],
      isMultiple: false,
    },
    scribeNoteVerbosity: {
      options: [
        { label: 'Detailed', value: 'detailed' },
        { label: 'Moderate', value: 'moderate' },
        { label: 'Concise', value: 'concise' },
      ],
      isMultiple: false,
    },
  };

  const handleIntakePlansSelectionChange = (event) => {
    let newSelectedPlans = event.target.value;
    if (newSelectedPlans.includes('*')) {
      setSelectedPlans(intakePlans);
      setValues({ name: 'intakePlans', category: 'medical', value: '*' });
      return;
    }
    newSelectedPlans = newSelectedPlans.filter(Boolean);
    setSelectedPlans(newSelectedPlans);
    const intakePlanValue = newSelectedPlans.map((plan) => plan.id).join(',');
    setValues({ name: 'intakePlans', category: 'medical', value: intakePlanValue });
  };

  const handleQrCodeIntakePlanSelectionChange = (event) => {
    const selectedPlan = event.target.value;
    const selectedPlanValue = selectedPlan.id.toString();

    setQrCodeIntakePlan(selectedPlan);

    setValues({ name: 'qrCodeIntakePlan', category: 'medical', value: selectedPlanValue });
  };

  async function getIntakePlans() {
    try {
      const intakePlanList = (await apiClient.getIntakePlans()).data;
      return intakePlanList;
    } catch (error) {
      return [];
    }
  }

  async function fetchSiteData(id) {
    const site = await apiClient.getSite(id);
    return { settings: site.data.settings, defaultSettings: site.data.defaultSettings };
  }

  async function fetchCustomerData(id) {
    const customer = await apiClient.getCustomer(id);
    return customer.data.settings;
  }

  async function getIntakePlansAssignedToCustomerOrSite(type, settingName) {
    try {
      let setting = null;
      if (type === 'site') {
        const { settings, defaultSettings } = await fetchSiteData(siteId);
        setting = settings.find((s) => s.name === settingName);
        if (!setting) {
          setting = defaultSettings.find((s) => s.name === settingName);
        }
      } else {
        const settings = await fetchCustomerData(customerId);
        setting = settings.find((s) => s.name === settingName);
      }
      return setting ? setting.value : null;
    } catch (error) {
      setErrorMessage(`Failed to fetch ${settingName}`);
      return null;
    }
  }

  const getSelectedPlans = (intakePlanIds, intakePlanList) => {
    if (intakePlanIds[0] === '*') {
      return intakePlanList;
    }
    if (intakePlanIds[0] === '') {
      return [];
    }
    const plans = intakePlanList.filter((intakePlan) => (
      intakePlanIds.includes(intakePlan.id.toString())));
    return plans;
  };

  useEffect(() => {
    if (values.name === 'intakePlans' || appointmentTypeSettingList.includes(values.name)) {
      getIntakePlans().then((intakePlanList) => {
        setIntakePlans(intakePlanList);
        getIntakePlansAssignedToCustomerOrSite(contextType, 'intakePlans').then((intakePlansList) => {
          const intakePlanNames = intakePlansList.split(',');
          const plans = getSelectedPlans(intakePlanNames, intakePlanList);
          setSelectedPlans(plans);
        });
      });
    }

    if (values.name === 'qrCodeIntakePlan') {
      getIntakePlans().then((intakePlanList) => {
        setIntakePlans(intakePlanList);
        getIntakePlansAssignedToCustomerOrSite(contextType, 'qrCodeIntakePlan').then((intakePlan) => {
          const plans = getSelectedPlans([intakePlan], intakePlanList);
          setQrCodeIntakePlan(plans[0]);
        });
      });
    }
  }, [values.name]);

  const renderOption = () => {
    if (appointmentTypeSettingList.includes(values.name)) {
      return (
        <Grid container item spacing={2} direction="row" alignItems="center">
          <Grid item>
            <AppointmentTypeSettingButton
              appointmentTypeSetting={values}
              selectedPlans={selectedPlans}
              refreshParent={refreshParent}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
            />
          </Grid>
        </Grid>
      );
    }
    if (values.name === 'intakePlans') {
      return (
        <Grid container item spacing={2} direction="row" alignItems="center">
          <Grid item>
            <Controls.Select
              sx={{ minWidth: '200px', maxWidth: '400px' }}
              label="Value"
              name="value"
              value={selectedPlans}
              onChange={handleIntakePlansSelectionChange}
              error={null}
              multiple
              renderValue={() => {
                if (selectedPlans.length === 0) {
                  return <em>None</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedPlans?.map((plan) => (
                      <Chip
                        key={plan.id}
                        label={generateIntakePlanLabel(plan)}
                      />
                    ))}
                  </Box>
                );
              }}
            >
              <MenuItem key="All Intake Plans" value="*">
                All Intake Plans
              </MenuItem>
              {intakePlans.map((plan) => (
                <MenuItem key={plan.referenceCode} value={plan}>
                  {generateIntakePlanLabel(plan)}
                </MenuItem>
              ))}
            </Controls.Select>
          </Grid>
          {!newSetting && (
            <Grid item>
              <Controls.Button
                text="Save"
                onClick={handleSaveClick}
              />
            </Grid>
          )}
        </Grid>
      );
    }
    if (values.name === 'qrCodeIntakePlan') {
      return (
        <Grid container item spacing={2} direction="row" alignItems="center">
          <Grid item>
            <Controls.Select
              sx={{ minWidth: '200px', maxWidth: '400px' }}
              label="Value"
              name="value"
              value={qrCodeIntakePlan}
              onChange={handleQrCodeIntakePlanSelectionChange}
              error={null}
              renderValue={() => {
                if (!qrCodeIntakePlan) {
                  return <em>None</em>;
                }
                return (
                  <Chip key={qrCodeIntakePlan.id} label={qrCodeIntakePlan.referenceCode} />
                );
              }}
            >
              {intakePlans.map((plan) => (
                <MenuItem key={plan.uniqueName} value={plan}>
                  {generateIntakePlanLabel(plan)}
                </MenuItem>
              ))}
            </Controls.Select>
          </Grid>
          {!newSetting && (
            <Grid item>
              <Controls.Button
                text="Save"
                onClick={handleSaveClick}
              />
            </Grid>
          )}
        </Grid>
      );
    }
    if (booleanSettingList.includes(values.name)) {
      return (
        <Grid container item spacing={2} direction="row" alignItems="center">
          <Grid item>
            <SettingsInputSwitcher
              type="switch"
              setting={values}
              refreshParent={refreshParent}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              defaultOption={{ value: values.name === 'consentPageLogoOn' }}
              contextType={contextType}
            />
          </Grid>
        </Grid>
      );
    }
    if (values.name === 'accuroSendDays' || values.name === 'qrCodeActiveDays') {
      return (
        <Grid container item spacing={2} direction="row" alignItems="center">
          <Grid item>
            <SettingsInputSwitcher
              type="toggle"
              setting={values}
              refreshParent={refreshParent}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              options={[
                { name: 'Mon', value: 1 },
                { name: 'Tues', value: 2 },
                { name: 'Wed', value: 3 },
                { name: 'Thurs', value: 4 },
                { name: 'Fri', value: 5 },
                { name: 'Sat', value: 6 },
                { name: 'Sun', value: 7 },
              ]}
              defaultOption={{ name: 'All', value: '' }}
              contextType={contextType}
            />
          </Grid>
        </Grid>
      );
    }
    if (logoSettingList.includes(values.name)) {
      return (
        <Grid container item spacing={2} direction="row" alignItems="center">
          <Grid item>
            <SettingsInputSwitcher
              type="logoUpload"
              setting={values}
              setValues={setValues}
              refreshParent={refreshParent}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              isCreateMode={isCreateMode}
              contextType={contextType}
            />
          </Grid>
        </Grid>
      );
    }

    if (values.name.toLowerCase().includes('color') || values.name === 'primaryTheme') {
      return (
        <Grid container item spacing={2} direction="row" alignItems="center">
          <Grid item>
            <SettingsInputSwitcher
              type="colorPicker"
              setting={values}
              refreshParent={refreshParent}
              setErrorMessage={setErrorMessage}
              setSuccessMessage={setSuccessMessage}
              isCreateMode={isCreateMode}
              contextType={contextType}
            />
          </Grid>
        </Grid>
      );
    }
    if (dropdownSettingList.includes(values.name)) {
      const { options = [], isMultiple = false } = optionsMapping[values.name];
      return (
        <Grid container item spacing={2} direction="row" alignItems="center">
          <Grid item>
            <SettingsInputSwitcher
              type="dropdown"
              setting={values}
              options={options}
              isMultiple={isMultiple}
              contextType={contextType}
              setSuccessMessage={setSuccessMessage}
              refreshParent={refreshParent}
              setErrorMessage={setErrorMessage}
            />
          </Grid>
        </Grid>
      );
    }

    const isMultiline = multilineSettingList.includes(values.name);
    return (
      <Grid container item spacing={2} direction="row" alignItems="center">
        <Grid item>
          <SettingsInputSwitcher
            type="text"
            setting={values}
            setValues={setValues}
            refreshParent={refreshParent}
            setErrorMessage={setErrorMessage}
            setSuccessMessage={setSuccessMessage}
            isCreateMode={isCreateMode}
            contextType={contextType}
            multiline={isMultiline}
            rows={isMultiline ? 10 : undefined}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container spacing={2} direction="row" alignItems="center">
      {renderOption()}
    </Grid>
  );
}

EditSettingsOptions.propTypes = {
  setValues: PropTypes.func.isRequired,
  handleSaveClick: PropTypes.func.isRequired,
  values: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  contextType: PropTypes.oneOf(['customer', 'site']).isRequired,
  newSetting: PropTypes.bool,
  refreshParent: PropTypes.func,
  setErrorMessage: PropTypes.func,
  setSuccessMessage: PropTypes.func,
  isCreateMode: PropTypes.bool,
};

EditSettingsOptions.defaultProps = {
  newSetting: false,
  refreshParent: null,
  setErrorMessage: null,
  setSuccessMessage: null,
  isCreateMode: false,
};
